import React, { useEffect, useState } from 'react';
import { Sparkles, Save, Info, MessageCircle, Instagram, Phone, Mail, FileText, MessageSquare, Repeat, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// One row per switch under Organization.aiAutoReply.channels. `forms`
// covers every submission-style source (web forms + lead ads) — those
// have no thread to reply into, so the AI answers by email or SMS.
const CHANNELS = [
  { key: 'messenger', label: 'Facebook Messenger', icon: MessageCircle, hint: 'Replies in the DM thread. Needs a connected Facebook Page.' },
  { key: 'instagram', label: 'Instagram DM', icon: Instagram, hint: 'Replies in the DM thread. Needs the Page\'s linked Instagram account.' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, hint: 'Free-form reply inside the 24-hour customer window. Needs WhatsApp Business connected.' },
  { key: 'sms', label: 'SMS', icon: Phone, hint: 'Short plain-text reply to the number that texted you. Needs Gain Rhino VoIP with SMS.' },
  { key: 'email', label: 'Email', icon: Mail, hint: 'Threaded reply from your SMTP sender. Skipped for leads who unsubscribed.' },
  { key: 'forms', label: 'Web forms & lead ads', icon: FileText, hint: 'First response to a form submission — by email if the lead left one, otherwise by SMS.' },
];
const DEFAULT_CHANNELS = Object.fromEntries(CHANNELS.map((c) => [c.key, true]));

// Organization.aiAutoReply.followUps — see server/services/followUpService.js.
const DEFAULT_FOLLOW_UPS = {
  enabled: false,
  days: [1, 3, 7, 14],
  stageIds: [],
  channel: 'auto',
  minTalkSeconds: 60,
  sendWindow: { start: '09:00', end: '21:00' },
  socialInWindow: true,
};
const MAX_FOLLOW_UP_STEPS = 6;

// Org-admin settings page for the after-hours AI auto-reply feature.
// Reads/writes Organization.aiAutoReply via two endpoints:
//   GET  /api/organizations/info        → prefill form
//   PUT  /api/organizations/ai-auto-reply
//
// Renders a single column: enable toggle + business-hour grid + day
// picker + confidence threshold + optional signature + optional safe
// acknowledgment (sent whenever the hallucination guard rejects a
// draft). The amber info strip at the top explains the guard.
export default function AIAutoReplySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    enabled: false,
    businessHours: { start: '09:00', end: '18:00', days: [1, 2, 3, 4, 5] },
    confidenceThreshold: 0.6,
    signature: '',
    fallbackMessage: '',
    channels: { ...DEFAULT_CHANNELS },
    followUps: { ...DEFAULT_FOLLOW_UPS },
  });
  const [orgName, setOrgName] = useState('');
  // [{ pipeline: {_id, name}, stages: [{_id, name, closedType}] }]
  const [pipelines, setPipelines] = useState([]);
  const [newDay, setNewDay] = useState('');
  const [tz, setTz] = useState('UTC');

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, pipelineRes] = await Promise.all([
          api.get('/organizations/info'),
          api.get('/pipelines').catch(() => ({ data: [] })),
        ]);
        const aar = data?.organization?.aiAutoReply || {};
        const groups = await Promise.all((pipelineRes.data || []).map(async (p) => {
          const { data: stages } = await api.get(`/pipelines/${p._id}/stages`).catch(() => ({ data: [] }));
          // Do Not Call stages can never receive automated messages.
          return { pipeline: p, stages: (stages || []).filter((st) => st.closedType !== 'do_not_call') };
        }));
        setPipelines(groups);
        const fu = aar.followUps || {};
        const savedStages = (fu.stageIds || []).map(String);
        setForm({
          enabled: !!aar.enabled,
          businessHours: {
            start: aar.businessHours?.start || '09:00',
            end:   aar.businessHours?.end   || '18:00',
            days:  Array.isArray(aar.businessHours?.days) && aar.businessHours.days.length
              ? aar.businessHours.days : [1, 2, 3, 4, 5],
          },
          confidenceThreshold: typeof aar.confidenceThreshold === 'number' ? aar.confidenceThreshold : 0.6,
          signature: aar.signature || '',
          fallbackMessage: aar.fallbackMessage || '',
          channels: { ...DEFAULT_CHANNELS, ...(aar.channels || {}) },
          followUps: {
            enabled: !!fu.enabled,
            days: Array.isArray(fu.days) && fu.days.length ? fu.days : DEFAULT_FOLLOW_UPS.days,
            // Nothing picked yet → suggest each pipeline's first open stage.
            stageIds: savedStages.length
              ? savedStages
              : groups.map((g) => g.stages.find((st) => !st.isClosed)?._id).filter(Boolean).map(String),
            channel: fu.channel || DEFAULT_FOLLOW_UPS.channel,
            minTalkSeconds: typeof fu.minTalkSeconds === 'number' ? fu.minTalkSeconds : DEFAULT_FOLLOW_UPS.minTalkSeconds,
            socialInWindow: fu.socialInWindow !== false,
            sendWindow: {
              start: fu.sendWindow?.start || DEFAULT_FOLLOW_UPS.sendWindow.start,
              end: fu.sendWindow?.end || DEFAULT_FOLLOW_UPS.sendWindow.end,
            },
          },
        });
        setTz(data?.organization?.settings?.timezone || 'UTC');
        setOrgName(data?.organization?.name || '');
      } catch (err) {
        toast.error('Could not load AI auto-reply settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleDay = (n) => {
    setForm((f) => {
      const has = f.businessHours.days.includes(n);
      const days = has ? f.businessHours.days.filter((d) => d !== n) : [...f.businessHours.days, n].sort();
      return { ...f, businessHours: { ...f.businessHours, days } };
    });
  };

  const toggleChannel = (key) => {
    setForm((f) => ({ ...f, channels: { ...f.channels, [key]: !f.channels[key] } }));
  };

  const setFollowUps = (patch) => setForm((f) => ({ ...f, followUps: { ...f.followUps, ...patch } }));

  const toggleStage = (id) => {
    const ids = form.followUps.stageIds;
    setFollowUps({ stageIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  };

  const addFollowUpDay = () => {
    const d = Number(newDay);
    if (!Number.isInteger(d) || d < 1 || d > 60) {
      toast.error('Pick a day between 1 and 60');
      return;
    }
    if (form.followUps.days.includes(d)) {
      toast.error(`Day ${d} is already in the sequence`);
      return;
    }
    setFollowUps({ days: [...form.followUps.days, d].sort((a, b) => a - b) });
    setNewDay('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/organizations/ai-auto-reply', form);
      toast.success('AI auto-reply settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={20} className="text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">AI Auto-Reply</h1>
        </div>
        <p className="text-sm text-gray-600">
          When customers reach out on any connected channel — Messenger, Instagram, WhatsApp, SMS, email, or a web form / lead ad — outside your team's working hours, AI keeps the conversation alive until someone is back online. Once an operator claims the lead — or the clock crosses back into business hours — the AI stops and the human takes over.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex items-start gap-2">
        <Info size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          AI only answers from your knowledge base. Before a reply goes out it must clear the confidence threshold below, every number, price, time, phone, link and time promise in it must appear in your knowledge base or the conversation, and a second AI model must confirm it is fully supported. If any check fails, the customer gets the safe acknowledgment below instead (at most once every 12 hours). Every auto-sent message is tagged in the thread — tap the AI badge to see its sources and checks.
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">Enable after-hours auto-reply</div>
            <div className="text-xs text-gray-600">When off, after-hours messages just sit in the inbox until morning.</div>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-colors relative
              after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
          </label>
        </div>

        <div className="border-t border-gray-100" />

        {/* Business hours */}
        <div>
          <div className="text-sm font-semibold text-gray-900 mb-1">Business hours <span className="text-xs font-normal text-gray-500">({tz})</span></div>
          <div className="text-xs text-gray-600 mb-3">Inside these hours, your team is expected to be online and AI stays out of the way.</div>
          <div className="flex items-center gap-3">
            <input
              type="time"
              value={form.businessHours.start}
              onChange={(e) => setForm((f) => ({ ...f, businessHours: { ...f.businessHours, start: e.target.value } }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <span className="text-sm text-gray-500">to</span>
            <input
              type="time"
              value={form.businessHours.end}
              onChange={(e) => setForm((f) => ({ ...f, businessHours: { ...f.businessHours, end: e.target.value } }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div className="mt-3 flex gap-1.5 flex-wrap">
            {DOW_LABELS.map((lbl, i) => {
              const on = form.businessHours.days.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    on
                      ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Channels */}
        <div>
          <div className="text-sm font-semibold text-gray-900 mb-1">Channels</div>
          <div className="text-xs text-gray-600 mb-3">
            Pick where the AI is allowed to answer. A channel that isn't set up yet is skipped automatically, so leaving it on is harmless.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CHANNELS.map(({ key, label, icon: Icon, hint }) => {
              const on = form.channels[key] !== false;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleChannel(key)}
                  aria-pressed={on}
                  className={`flex items-start gap-3 text-left p-3 rounded-md border transition-colors ${
                    on ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <span className={`mt-0.5 flex-shrink-0 ${on ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-medium ${on ? 'text-gray-900' : 'text-gray-600'}`}>{label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>
                  </span>
                  <span className={`mt-1 flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                    on ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white'
                  }`}>{on ? '✓' : ''}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-t border-gray-100" />

        {/* Confidence */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-semibold text-gray-900">Confidence threshold</div>
            <div className="text-xs font-mono text-gray-700">{Math.round(form.confidenceThreshold * 100)}%</div>
          </div>
          <div className="text-xs text-gray-600 mb-2">
            Drafts the AI scores below this are replaced by the safe acknowledgment. Higher = more acknowledgments, fewer AI answers. 60% is a sensible starting point.
          </div>
          <input
            type="range"
            min="0" max="1" step="0.05"
            value={form.confidenceThreshold}
            onChange={(e) => setForm((f) => ({ ...f, confidenceThreshold: Number(e.target.value) }))}
            className="w-full"
          />
        </div>

        <div className="border-t border-gray-100" />

        {/* Signature */}
        <div>
          <div className="text-sm font-semibold text-gray-900 mb-1">Opening line (optional)</div>
          <div className="text-xs text-gray-600 mb-2">
            Added to the top of every AI-written reply so customers know they're talking to AI before a human takes over. It is not added to the safe acknowledgment or to follow-ups. Keep it short (e.g. "Hi! This is an AI-assisted reply."). Leave blank to send replies as plain text.
          </div>
          <textarea
            rows={2}
            value={form.signature}
            onChange={(e) => setForm((f) => ({ ...f, signature: e.target.value }))}
            placeholder="Hi! This is an AI-assisted reply while our team is away."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
          />
        </div>

        <div className="border-t border-gray-100" />

        {/* Safe acknowledgment */}
        <div>
          <div className="flex items-center justify-between mb-1 gap-2">
            <div className="text-sm font-semibold text-gray-900">Safe acknowledgment (optional)</div>
            <div className="text-xs font-mono text-gray-500">{form.fallbackMessage.length}/300</div>
          </div>
          <div className="text-xs text-gray-600 mb-2">
            Sent instead of an AI answer when the question isn't covered by your knowledge base or the answer can't be verified. Leave blank to use the default, which names your business and works out when the team is back (today, tomorrow or on Monday) every time. If you write your own, don't name a day or time, since it is sent at all hours. If the customer sounds urgent (pain, bleeding, a leak, no heat…), the answer from your knowledge base entry tagged "emergency" is added automatically — make sure you have one.
          </div>
          <textarea
            rows={2}
            maxLength={300}
            value={form.fallbackMessage}
            onChange={(e) => setForm((f) => ({ ...f, fallbackMessage: e.target.value }))}
            placeholder={`Thanks for reaching out${orgName ? ` to ${orgName}` : ''}! Our team is offline right now and will get back to you tomorrow at 9:00 AM.`}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
          />
        </div>
      </div>

      {/* Follow-up sequence */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Repeat size={15} className="text-indigo-600" /> Follow-up sequence
            </div>
            <div className="text-xs text-gray-600 mt-1">
              AI checks in with new leads that haven't replied, by SMS or email, until they reply or your team takes them out of the stages below.
            </div>
          </div>
          <label className="inline-flex items-center cursor-pointer flex-shrink-0">
            <input
              type="checkbox"
              checked={form.followUps.enabled}
              onChange={(e) => setFollowUps({ enabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-colors relative
              after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
          </label>
        </div>

        <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-700 space-y-1">
          <div><span className="font-semibold">Pauses</span> when the lead moves to a stage that isn't selected below (for example after your team talks to them and moves it to Contacted), and <span className="font-semibold">resumes</span> from the next step if it moves back.</div>
          <div><span className="font-semibold">Stops for good</span> when the customer replies or calls, when your team has a call of the length below through Gain Rhino, or on Do Not Call / STOP / unsubscribe. You can also stop it for a single lead from the lead's page.</div>
          <div><span className="font-semibold">Turning follow-ups off</span> ends every running sequence. When you turn them back on, they start with the next new lead.</div>
        </div>

        <div className={`space-y-5 ${form.followUps.enabled ? '' : 'opacity-60'}`}>
          {/* Schedule */}
          <div>
            <div className="text-sm font-semibold text-gray-900 mb-1">Schedule</div>
            <div className="text-xs text-gray-600 mb-2">Days after the lead came in. Up to {MAX_FOLLOW_UP_STEPS} follow-ups.</div>
            <div className="flex flex-wrap items-center gap-2">
              {form.followUps.days.map((d) => (
                <span key={d} className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-xs font-medium text-indigo-800">
                  Day {d}
                  <button
                    type="button"
                    onClick={() => setFollowUps({ days: form.followUps.days.filter((x) => x !== d) })}
                    disabled={form.followUps.days.length <= 1}
                    className="p-0.5 rounded-full hover:bg-indigo-100 disabled:opacity-30"
                    aria-label={`Remove day ${d}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {form.followUps.days.length < MAX_FOLLOW_UP_STEPS && (
                <span className="inline-flex items-center gap-1">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={newDay}
                    onChange={(e) => setNewDay(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFollowUpDay(); } }}
                    placeholder="Day"
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-xs"
                  />
                  <button type="button" onClick={addFollowUpDay} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50">
                    <Plus size={12} /> Add
                  </button>
                </span>
              )}
            </div>
          </div>

          {/* Stages */}
          <div>
            <div className="text-sm font-semibold text-gray-900 mb-1">Stages</div>
            <div className="text-xs text-gray-600 mb-2">
              Follow-ups run while the lead is in one of these stages. Moving it anywhere else pauses them; moving it back resumes where it left off. Usually just your first stage, like New.
            </div>
            {pipelines.length === 0 && <div className="text-xs text-gray-500">No pipelines found.</div>}
            <div className="space-y-3">
              {pipelines.map(({ pipeline, stages }) => (
                <div key={pipeline._id}>
                  {pipelines.length > 1 && <div className="text-xs font-medium text-gray-500 mb-1.5">{pipeline.name}</div>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {stages.map((st) => {
                      const on = form.followUps.stageIds.includes(String(st._id));
                      return (
                        <label key={st._id} className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer ${on ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={on} onChange={() => toggleStage(String(st._id))} className="rounded text-indigo-600" />
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: st.color || '#6B7280' }} />
                          <span className="truncate">{st.name}</span>
                          {st.isClosed && <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400">closed</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {form.followUps.enabled && form.followUps.stageIds.length === 0 && (
              <div className="text-xs text-red-600 mt-2">Pick at least one stage.</div>
            )}
          </div>

          {/* Messenger / Instagram / WhatsApp in-window check-in */}
          <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={form.followUps.socialInWindow}
              onChange={(e) => setFollowUps({ socialInWindow: e.target.checked })}
              className="mt-0.5 rounded text-indigo-600"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">First check-in on Messenger, Instagram or WhatsApp</span>
              <span className="block text-xs text-gray-600 mt-0.5">
                Leads who wrote in on one of these get their first follow-up there, about 20 hours after their message, before Meta's 24-hour reply window closes. Meta doesn't allow automated messages after that, so the remaining follow-ups go by SMS or email, or stop if the lead has neither.
              </span>
            </span>
          </label>

          {/* Channel + window + call threshold */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-semibold text-gray-900 mb-1">Send by</div>
              <select
                value={form.followUps.channel}
                onChange={(e) => setFollowUps({ channel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="auto">Automatic</option>
                <option value="sms">SMS only</option>
                <option value="email">Email only</option>
              </select>
              <div className="text-xs text-gray-500 mt-1">Automatic: email for leads who emailed you, SMS for everyone else, with the other as backup.</div>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900 mb-1">Send between <span className="text-xs font-normal text-gray-500">({tz})</span></div>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={form.followUps.sendWindow.start}
                  onChange={(e) => setFollowUps({ sendWindow: { ...form.followUps.sendWindow, start: e.target.value } })}
                  className="min-w-0 flex-1 px-2 py-2 border border-gray-300 rounded-md text-sm"
                />
                <span className="text-xs text-gray-500">to</span>
                <input
                  type="time"
                  value={form.followUps.sendWindow.end}
                  onChange={(e) => setFollowUps({ sendWindow: { ...form.followUps.sendWindow, end: e.target.value } })}
                  className="min-w-0 flex-1 px-2 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div className="text-xs text-gray-500 mt-1">Any day of the week, during or after business hours.</div>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900 mb-1">Call counts as reached after</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="600"
                  value={form.followUps.minTalkSeconds}
                  onChange={(e) => setFollowUps({ minTalkSeconds: Math.max(0, Math.min(600, Number(e.target.value) || 0)) })}
                  className="w-24 px-2 py-2 border border-gray-300 rounded-md text-sm"
                />
                <span className="text-xs text-gray-500">seconds</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">Shorter answered calls are usually voicemail and don't stop the sequence.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium"
        >
          <Save size={14} /> {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
}
